// A season as a campaign that tells a story. Each post, Story, email and text
// has a job (open, show the work, prove it, meet the team, help, the offer,
// last call, close), done in that order across the campaign's days, each in a
// different design with different words.
//
// Only real things: the salon's own photos, reviews, team, free times and
// vouchers. When there's nothing real for a job, the job is skipped. A
// design's example words (its prices, times, reviews, names) and stock photos
// never go into a campaign.
import moment from 'moment'
import { makePost, realPost, designsFor, isStock, restyle, FAMILY_ORDER } from '../../social/data/postTypes'
import { hasSeasonal, isSeasonal, seasonalFor, seasonalLayout, seasonalLayouts, withSalon } from '../../social/kits'
import { templateFor } from '../../plans/data/emailFor'
import { smsText, textLinks } from '../../plans/data/sms'
import { linesFor, emailWords, textWords, fill, inTone } from './words'
import { minutesOf } from '../../social/data/dates'
import { colourwayFor, startingLook } from './looks'

export const ROLES = {
  open: 'Open',
  show: 'Show the work',
  prove: 'Prove it',
  team: 'Meet the team',
  help: 'Help',
  offer: 'The offer',
  lastcall: 'Last call',
  close: 'Close',
}

const at = (day, h, m = 0) => moment(day).hours(h).minutes(m).seconds(0).milliseconds(0)
const iso = d => moment(d).format('YYYY-MM-DD')
// The day as said in a sentence: "tomorrow", "on Saturday", "on Friday 25 December"
export const said = (date, now) => {
  const d = moment(date).startOf('day')
  const diff = d.diff(moment(now).startOf('day'), 'days')
  if (diff === 0) return 'today'
  if (diff === 1) return 'tomorrow'
  if (diff > 1 && diff < 7) return `on ${d.format('dddd')}`
  return `on ${d.format('dddd D MMMM')}`
}
const WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten']
const count = (n, one, many) => `${WORDS[n] || n} ${n === 1 ? one : many}`
const cap = s => String(s || '').charAt(0).toUpperCase() + String(s || '').slice(1)
const firstName = s => String(s || '').trim().split(/\s+/)[0] || ''
const andList = list => list.join(', ').replace(/, ([^,]*)$/, ' and $1')
// Free times earliest first, as a post shows them
const sortTimes = list => (list || []).slice().sort((a, b) => (minutesOf(a) == null ? 1e4 : minutesOf(a)) - (minutesOf(b) == null ? 1e4 : minutesOf(b)))
// Days nobody wants a marketing email or text
export const restDay = (d) => { const m = moment(d); return (m.month() === 11 && (m.date() === 25 || m.date() === 26)) || (m.month() === 0 && m.date() === 1) }

// Designs a campaign doesn't use: they need things Bailey can't know (three
// facts about a stylist, three steps) or say something else ("This or that?")
const SKIP = { team: ['studioId', 'threeThings'], tips: ['steps', 'checklist'], question: ['myth'] }
const usable = type => designsFor(type).filter(d => !(d.layout && (SKIP[type] || []).includes(d.layout)))
const keyOf = (type, d) => `${type}|${d.family}|${d.layout || ''}`

// A different design every time, and a campaign that moves between the
// styles and the layouts rather than sitting in one: the salon's own style
// first, then a layout, then the next style. In the salon's brand look it
// keeps to their style, with layouts between.
export const designPicker = ({ family = 'editorial', keepStyle = false, used = new Set() } = {}) => {
  let n = 0
  const start = Math.max(0, FAMILY_ORDER.indexOf(family))
  const pick = (type) => {
    const all = usable(type)
    const styles = all.filter(d => d.family !== 'layouts')
    const layouts = all.filter(d => d.family === 'layouts')
    const style = FAMILY_ORDER[keepStyle ? start : (start + Math.floor(n / 2)) % FAMILY_ORDER.length]
    const wantLayout = n % 2 === 1 && layouts.length > 0
    const first = wantLayout ? layouts : styles.filter(d => d.family === style)
    const ordered = [...first, ...styles.filter(d => d.family === style), ...layouts, ...styles]
    const pick = ordered.find(d => !used.has(keyOf(type, d))) || ordered[0] || { family: 'editorial' }
    used.add(keyOf(type, pick))
    n += 1
    return pick
  }
  // How many of a job's designs haven't been used yet
  pick.left = type => usable(type).filter(d => !used.has(keyOf(type, d))).length
  return pick
}

// Fields a design would otherwise fill with its own example words
const BLANK = {
  giftVouchers: { from: '' },
  freeSlots: { service: '' },
  team: { facts: [], role: '', line: '' },
  tips: { number: '', steps: [], detail: '' },
  announce: { body: '' },
}
// A post made only from what's given: no example facts, no stock photos
export const safePost = (type, { ctx, look, design, format, content = {}, photos = {}, source }) => {
  const colourway = colourwayFor({ look, momentId: ctx.momentId, brandColour: ctx.brandColour }, design.family, type, design.layout)
  // realPost also empties every example fact of the kind that isn't given
  const p = realPost(type, {
    trade: ctx.trade, specialism: ctx.specialism || null, brandColour: look === 'brand' ? ctx.brandColour : null, family: design.family, layout: design.layout, colourway,
    format, source, content: { ...(BLANK[type] || {}), ...content }, photos,
  })
  return { ...p, status: 'draft' }
}

// The salon's photos of its own work, from its posts (never a stock photo)
export const photosFromPosts = (posts = []) => {
  const out = []
  ;(posts || []).forEach((p) => {
    if (!p || !p.photos || (p.type !== 'freshWork' && p.type !== 'beforeAfter')) return
    const ph = p.type === 'beforeAfter' ? p.photos.after : p.photos.main
    if (ph && ph.src && !isStock(ph.src) && !/^blob:/.test(ph.src) && !out.includes(ph.src)) out.push(ph.src)
  })
  return out
}

// How much a campaign of this length holds: at most two posts and four
// Stories a week, emails at most weekly, one text (two on the longest, a week
// apart). A season's text starts switched off: it costs credits and a season
// isn't a date people book for.
export const cadence = (days, kind) => ({
  posts: days <= 3 ? 1 : days <= 7 ? 2 : days <= 14 ? 3 : days <= 21 ? 4 : 5,
  stories: days <= 3 ? 1 : days <= 7 ? 2 : days <= 14 ? 3 : 4,
  emails: days >= 35 ? 3 : days >= 21 ? 2 : 1,
  texts: days >= 42 ? 2 : 1,
  textOn: kind !== 'season',
})

// The jobs in story order, for posts and for Stories
const ORDER = {
  gift: { posts: ['open', 'prove', 'help', 'team', 'show', 'lastcall'], stories: ['help', 'lastcall', 'prove', 'open'] },
  season: { posts: ['open', 'show', 'help', 'prove', 'team'], stories: ['help', 'show', 'open', 'team'] },
  occasion: { posts: ['open', 'show', 'prove', 'team', 'help', 'offer'], stories: ['offer', 'lastcall', 'close', 'help', 'open'] },
}

// Two posts in a row never do the same kind of thing (two voucher posts, two
// tips): swap the second with the next one that differs, keeping the first
// and the last in place
const noTwoInARow = (list) => {
  const out = list.slice()
  for (let i = 1; i < out.length - 1; i += 1) {
    if (out[i].post.type === out[i - 1].post.type) {
      const j = out.findIndex((x, k) => k > i && k < out.length - 1 && x.post.type !== out[i - 1].post.type)
      if (j > -1) { const t = out[i]; out[i] = out[j]; out[j] = t }
    }
  }
  return out
}

export const seasonCampaign = (m, ctx) => {
  const { now, salonName = '', tone = null } = ctx
  const today = moment(now).startOf('day')
  const first = moment.max(moment(m.start).startOf('day'), moment(today).add(1, 'day'))
  const last = moment(m.end).startOf('day')
  if (last.isBefore(first)) return null
  const days = last.diff(first, 'days') + 1

  // What's real
  const reviews = (ctx.reviews || []).filter(r => r && r.rating >= 4 && r.text && r.text.length >= 20 && r.text.length <= 320)
  const team = (ctx.team || []).filter(t => t && t.name && t.image && !isStock(t.image))
  const photos = (ctx.photos || []).filter(src => src && !isStock(src))
  const freeDays = (Array.isArray(ctx.week) ? ctx.week : [])
    .filter(w => w && w.count >= 2 && moment(w.date).isBetween(first, last, 'day', '[]') && moment(w.date).isAfter(today, 'day'))
    .sort((a, b) => b.freeHours - a.freeHours)
  const vouchers = Array.isArray(ctx.vouchers) ? ctx.vouchers : null // null: not known
  const prices = (vouchers || []).map(v => Number(v.fromPrice)).filter(n => n > 0)
  const lowest = prices.length ? Math.min(...prices) : null
  const fromLine = lowest ? `From ${ctx.currency || '£'}${lowest % 1 ? lowest.toFixed(2) : lowest}` : ''

  // A gifting season needs vouchers on sale online; without them it asks
  // people to book instead
  const gifting = m.kind === 'gift' && !(vouchers && !vouchers.length)
  const kind = m.kind === 'gift' ? (gifting ? 'gift' : 'occasion') : m.kind === 'season' ? 'season' : 'occasion'
  const isSeason = kind === 'season'
  const eventDate = m.eventDate || m.date
  const vars = when => ({ event: m.event || m.name, salon: salonName || 'us', when: said(eventDate, when || now) })
  const look = ctx.look || startingLook(m.id, ctx.brandColour)
  const pctx = { ...ctx, momentId: m.id }
  const pick = designPicker({ family: ctx.family, keepStyle: look === 'brand' })
  // In the season's look, its own designs open, remind and close (a gift
  // season's talk about vouchers, so only while the salon sells them online)
  const seasonal = look === 'season' && hasSeasonal(m.id) && (m.kind !== 'gift' || gifting)
  const seasonalUsed = new Set()
  const used = new Set()
  const line = (role, extra = {}) => {
    const list = (linesFor(m.id, role, ctx.trade, { gifting, isSeason, specialism: ctx.specialism }) || []).map(l => fill(l, { ...vars(), ...extra }))
    const l = list.find(x => !used.has(x.headline || x.line)) || null
    if (!l) return null
    used.add(l.headline || l.line)
    const out = {}
    Object.keys(l).forEach((k) => { out[k] = inTone(l[k], tone) })
    return out
  }
  const photosLeft = photos.slice()
  const reviewsLeft = reviews.slice()
  const teamLeft = team.slice()
  const daysLeft = freeDays.slice()
  const src = (role, format) => `moment:${m.key}:${role}:${format}`
  const book = 'Book online, link in bio'
  // Once every voucher design is used, the rest say it as an announcement
  // rather than repeat one
  const voucherPost = (l, format, role) => (pick.left('giftVouchers') ? safePost('giftVouchers', { ctx: pctx, look, format, design: pick('giftVouchers'), source: src(role, format), content: { eyebrow: l.eyebrow, headline: l.headline, from: fromLine, cta: 'Buy online, link in bio' } })
    : announce(l, format, role, 'Buy online, link in bio'))
  // Announcements too: in the season's own designs and words while there
  // are some (the close keeps the design's own sign-off), then the styles;
  // once those are used up, the job is skipped
  const announce = (l, format, role, cta = book) => {
    const layout = seasonal ? seasonalFor(m.id, role, seasonalUsed) : null
    if (layout) {
      seasonalUsed.add(layout)
      const words = withSalon(seasonalLayout(layout).content(), salonName)
      return safePost('announce', { ctx: pctx, look, format, design: { family: 'layouts', layout }, source: src(role, format), content: role === 'close' ? words : { ...words, cta } })
    }
    return !pick.left('announce') ? null
      : safePost('announce', { ctx: pctx, look, format, design: pick('announce'), source: src(role, format), content: { eyebrow: l.eyebrow, headline: l.headline, body: l.body || '', cta } })
  }
  const work = (l, format, role) => safePost('freshWork', { ctx: pctx, look, format, design: pick('freshWork'), source: src(role, format), content: { detail: l ? l.eyebrow : cap(m.event), headline: l ? l.headline : `${cap(m.event)} at *${salonName || 'the salon'}*.`, cta: book }, photos: { main: { src: photosLeft.shift(), fx: 0.5, fy: 0.4, zoom: 1 } } })

  // Each job as a post or a Story, or null when there's nothing real for it
  const make = {
    open: (format) => {
      const l = line('open')
      if (!l) return null
      if (gifting) return voucherPost(l, format, 'open')
      return announce(l, format, 'open')
    },
    show: (format) => (photosLeft.length ? work(line('show'), format, 'show') : null),
    prove: (format) => {
      const r = reviewsLeft.shift()
      if (!r) return null
      const l = line('prove')
      return safePost('review', { ctx: pctx, look, format, design: pick('review'), source: `review:${r.id}`, content: { headline: l ? l.headline : 'Kind *words*.', quote: r.text, name: firstName(r.author || r.firstName) || 'A client', service: r.service || '', rating: r.rating, cta: gifting ? 'Gift vouchers online, link in bio' : book } })
    },
    team: (format) => {
      const t = teamLeft.shift()
      if (!t) return null
      const name = firstName(t.name)
      const l = line('team', { name })
      return safePost('team', { ctx: pctx, look, format, design: pick('team'), source: `team:${t.id}:${m.key}`, content: { eyebrow: l ? l.eyebrow : 'Meet the team', name, role: t.role || '', line: l ? l.line : '', cta: `Book with ${name}, link in bio` }, photos: { main: { src: t.image, fx: 0.5, fy: 0.35, zoom: 1 } } })
    },
    help: (format) => {
      const l = line('help')
      if (!l) return null
      return safePost('tips', { ctx: pctx, look, format, design: pick('tips'), source: src('help', format), content: { label: l.label, headline: l.headline, body: l.body, cta: 'Save this for later' } })
    },
    offer: (format) => {
      if (gifting) {
        const l = line('offer')
        return l ? voucherPost(l, format, 'offer') : null
      }
      const d = daysLeft.shift()
      if (!d) return null
      const l = line('offer')
      const whose = d.who ? { staffId: d.who.id, staffName: d.who.firstName } : {}
      const post = safePost('freeSlots', { ctx: pctx, look, format, design: pick('freeSlots'), source: `slots:${d.date}:${m.key}`, content: { eyebrow: l ? l.eyebrow : cap(m.event), headline: l ? l.headline : 'Times for *you*.', date: d.date, times: sortTimes((d.times || []).slice(0, 3)), more: d.count > 3, cta: 'Book now, link in bio', ...whose } })
      return { ...post, day: d.date }
    },
    lastcall: (format) => {
      const l = line('lastcall')
      if (!l) return null
      return gifting ? voucherPost(l, format, 'lastcall') : announce(l, format, 'lastcall', 'Book now, link in bio')
    },
    close: (format) => {
      const l = line('close')
      return l ? announce(l, format, 'close', '') : null
    },
  }

  const plan = cadence(days, kind)
  const order = ORDER[kind]
  const items = []
  const inWindow = t => moment(t).isAfter(now) && moment(t).isSameOrBefore(moment(last).endOf('day'))
  const add = (it) => { if (it && inWindow(it.at)) items.push(it) }
  const dayAt = i => moment(first).add(Math.max(0, Math.min(days - 1, Math.round(i))), 'days')
  const clean = (post) => { const { day, ...rest } = post; return rest }

  // ─── Posts: the jobs in order, as many as the length allows ───
  let posts = []
  order.posts.forEach((role) => {
    if (posts.length >= plan.posts) return
    const p = make[role]('post')
    if (p) posts.push({ role, post: p })
  })
  posts = noTwoInARow(posts)
  posts.forEach(({ role, post }, i) => {
    const n = posts.length
    // Spread across the days; the offer and last call near the end
    let d = n === 1 ? 0 : (i * (days - 1)) / n
    if ((role === 'offer' || role === 'lastcall') && n > 1) d = Math.max(d, days - 4)
    const when = role === 'offer' && post.day && moment(post.day).subtract(1, 'day').isSameOrAfter(first, 'day') ? moment(post.day).subtract(1, 'day') : dayAt(d)
    add({ id: `post${i + 1}`, channel: 'post', role, at: at(when, 12, 30).toISOString(), on: true, title: `${ROLES[role]} post`, post: clean(post) })
  })

  // ─── Stories: the jobs the posts didn't do, and a close on the day ───
  const stories = []
  order.stories.forEach((role) => {
    if (stories.length >= plan.stories) return
    const p = make[role]('story')
    if (p) stories.push({ role, post: p })
  })
  const RANK = Object.keys(ROLES)
  stories.sort((a, b) => RANK.indexOf(a.role) - RANK.indexOf(b.role))
  const spread = stories.filter(x => !['close', 'lastcall'].includes(x.role) && !(x.role === 'offer' && x.post.day))
  stories.forEach(({ role, post }, i) => {
    let when
    if (role === 'close') when = at(moment(eventDate).isAfter(last, 'day') ? last : eventDate, 9)
    else if (role === 'lastcall') when = at(moment(last).subtract(kind === 'gift' ? 0 : 1, 'day'), 8)
    else if (role === 'offer' && post.day) when = at(post.day, 8)
    else when = at(dayAt(((spread.findIndex(x => x.post === post) + 0.5) * (days - 1)) / Math.max(1, spread.length)), 18)
    add({ id: `story${i + 1}`, channel: 'story', role, at: when.toISOString(), on: true, title: `${ROLES[role]} Story`, post: clean(post) })
  })

  // ─── Emails: open the campaign; on longer ones, the middle and last days ───
  const button = gifting ? { button: 'Buy a gift voucher', buttonUrl: '{{GiftCardLink}}' } : { button: 'Book now', buttonUrl: '{{BookingLink}}' }
  const stages = plan.emails === 3 ? [['open', 0], ['mid', Math.floor(days / 2)], ['last', days - 4]]
    : plan.emails === 2 ? [['open', 0], [isSeason ? 'mid' : 'last', Math.max(7, days - 8)]]
      : [['open', 0]]
  const heroPhoto = photos[photos.length - 1]
  stages.forEach(([stage, d], i) => {
    const w = fill(emailWords(m.id, stage, { gifting, isSeason }), vars(dayAt(d)))
    const role = stage === 'open' ? 'open' : stage === 'mid' ? 'offer' : 'lastcall'
    add({
      id: i ? `email${i + 1}` : 'email', channel: 'email', role, at: at(dayAt(d), 10).toISOString(), on: true, title: stage === 'open' ? 'Opening email' : stage === 'mid' ? 'Halfway email' : 'Last days email',
      email: {
        templateId: templateFor(gifting ? 'moment' : 'fill', ctx.trade),
        subject: inTone(w.subject, tone),
        preheader: w.preheader,
        // The salon's own photo when it has one; otherwise words only, so no
        // stock photo of someone else's work goes out as theirs
        hero: {
          eyebrow: w.eyebrow, headline: inTone(w.headline, tone), body: inTone(w.body, tone), ...button, button: w.button || button.button,
          ...(gifting ? {} : heroPhoto && i === 0 ? { image: heroPhoto, imageAlt: `Recent work at ${salonName || 'the salon'}` } : { variant: 'type', image: null }),
        },
      },
    })
  })

  // ─── The text: a last call a few days before the end, in the evening. A
  // season's says it's here, so it goes near the start ───
  const end = kind === 'gift' ? 3 : 4
  const textDays = isSeason ? [Math.min(2, days - 1)]
    : plan.texts === 2 ? [days - end - 7, days - end] : [Math.max(Math.min(2, days - 1), days - end)]
  textDays.forEach((d, i) => {
    let textAt = at(dayAt(d), 18)
    // A last call goes the evening before the day, or that morning: at 6pm
    // on the day itself it's too late to book or to buy
    if (!isSeason && !textAt.isBefore(moment(eventDate).startOf('day'))) {
      const eve = at(moment(eventDate).subtract(1, 'day'), 18)
      textAt = eve.isAfter(now) ? eve : at(eventDate, 9)
    }
    const words = fill(textWords(m.id, { gifting, isSeason, early: textDays.length > 1 && i === 0 }), vars(textAt))
    const t = smsText([textLinks(words, ctx.links || {})], salonName)
    add({ id: i ? `sms${i + 1}` : 'sms', channel: 'sms', role: isSeason ? 'open' : 'lastcall', at: textAt.toISOString(), on: plan.textOn, title: isSeason ? 'Season text' : 'Last call text', message: t.message })
  })

  // Nobody to send to yet (a new account, nobody opted in to marketing):
  // posts and Stories only, never an email or a text to no one
  if (ctx.noClients) {
    for (let i = items.length - 1; i >= 0; i -= 1) if (items[i].channel === 'email' || items[i].channel === 'sms') items.splice(i, 1)
  }
  // A gift is bought before the day: on a gift date itself, nothing goes
  // out after the morning (a 6pm "it's today" text is too late to act on).
  // The evening before when there's time, otherwise first thing.
  if (kind === 'gift') {
    items.forEach((it) => {
      const t = moment(it.at)
      if (!t.isSame(eventDate, 'day') || t.hours() < 12) return
      const eve = at(moment(eventDate).subtract(1, 'day'), 18)
      const sameChannel = items.some(o => o !== it && o.channel === it.channel && moment(o.at).isSame(eve, 'day'))
      it.at = (eve.isAfter(now) && !sameChannel ? eve : at(eventDate, 8)).toISOString()
    })
  }
  // Nothing goes out on Christmas Day, Boxing Day or New Year's Day: not an
  // email, not a text, not a free-times Story at 8am on the 25th
  items.forEach((it) => {
    let t = moment(it.at)
    if (!restDay(t)) return
    // A day's free times belong to that day: dropped, never moved
    if (it.post && it.post.content && it.post.content.date) { it.restDay = true; return }
    while (restDay(t)) t = t.add(t.isBefore(moment(last).subtract(1, 'day')) ? 1 : -1, 'day')
    if (inWindow(t)) it.at = t.toISOString()
    else it.restDay = true
  })
  for (let i = items.length - 1; i >= 0; i -= 1) if (items[i].restDay) items.splice(i, 1)
  if (!items.length) return null
  const n = ch => items.filter(i => i.channel === ch && i.on).length
  const parts = [
    n('post') && count(n('post'), 'post', 'posts'),
    n('story') && count(n('story'), 'Story', 'Stories'),
    n('email') && count(n('email'), 'email', 'emails'),
    n('sms') && count(n('sms'), 'text', 'texts'),
  ].filter(Boolean)
  const notes = []
  if (m.kind === 'gift' && !gifting) notes.push('You don’t sell gift vouchers online yet, so this campaign asks people to book instead. Add a voucher in Catalogue and Bailey will sell them next time.')
  if (gifting && !lowest) notes.push('Your vouchers don’t have set amounts, so the voucher posts don’t show a price.')
  if (!photos.length) notes.push('Share a few photos of your work in Social and Bailey will show them off in campaigns.')
  if (ctx.noClients) notes.push('Emails and texts join in once you have clients who’ve said yes to marketing. Until then, posts and Stories reach new people.')
  if (isSeason && !plan.textOn && !ctx.noClients) notes.push('A text is ready but switched off. Texts come out of your message balance, so it’s yours to switch on.')
  const title = m.kind === 'gift' && !gifting ? cap(m.event) : gifting && !/voucher/i.test(m.name) ? `${m.name} gift vouchers` : m.name
  return {
    key: `moment:${m.key}`,
    kind: 'moment',
    title,
    why: `${title} runs ${first.format('ddd D MMM')} to ${last.format('ddd D MMM')}: ${andList(parts)}, each doing a different job.`,
    notes,
    audience: { segment: 'all', label: 'All your clients', count: null },
    items: items.sort((a, b) => new Date(a.at) - new Date(b.at)),
    start: iso(first),
    end: iso(last),
    moment: m.id,
    occasion: kind,
    event: m.event || m.name,
    eventDate: iso(eventDate),
    palette: m.palette || null,
    look,
    score: 80 - Math.max(0, first.diff(today, 'days') - 3),
  }
}

// ─── Variety: what repeats inside a campaign ───
const designOf = p => (p ? `${p.type}|${p.family}|${p.layout || ''}|${p.format || 'post'}` : '')
const plainWords = s => String(s || '').replace(/\*/g, '').replace(/\s+/g, ' ').trim().toLowerCase()
const wordsOf = (it) => {
  if (it.post) { const c = it.post.content || {}; return plainWords(c.headline || c.quote || c.title || c.name) }
  if (it.email) return plainWords(it.email.subject)
  return plainWords(String(it.message || '').split('\n')[0])
}
// { [itemId]: { with: itemId, what: 'words' | 'design' } } for items that
// repeat one before them (switched on only)
export const sameness = (items = []) => {
  const on = items.filter(i => i.on).sort((a, b) => new Date(a.at) - new Date(b.at))
  const out = {}
  on.forEach((it, i) => {
    const before = on.slice(0, i)
    const w = wordsOf(it)
    const sameWords = w && before.find(o => o.channel === it.channel && wordsOf(o) === w)
    const sameDesign = it.post && before.find(o => o.post && designOf(o.post) === designOf(it.post))
    if (sameWords) out[it.id] = { with: sameWords.id, what: 'words' }
    else if (sameDesign) out[it.id] = { with: sameDesign.id, what: 'design' }
  })
  return out
}

// Try another: the same job and words in the next design no other post in
// the campaign is wearing
export const tryAnother = (item, items, { look = 'own', momentId = null, brandColour = null } = {}) => {
  if (!item || !item.post) return item
  if (isSeasonal(item.post.layout)) return anotherSeasonal(item, items, momentId)
  const { type } = item.post
  const all = usable(type)
  const format = item.post.format || 'post'
  const taken = new Set(items.filter(i => i.post && i.id !== item.id && (i.post.format || 'post') === format).map(i => keyOf(i.post.type, i.post)))
  const idx = all.findIndex(d => d.family === item.post.family && (d.layout || '') === (item.post.layout || ''))
  for (let k = 1; k <= all.length; k += 1) {
    const d = all[(idx + k) % all.length]
    if (!taken.has(keyOf(type, d)) || k === all.length) {
      const colourway = colourwayFor({ look, momentId, brandColour }, d.family, type, d.layout)
      const next = makePost(type, { trade: item.post.trade, brandColour: look === 'brand' ? brandColour : null, family: d.family, layout: d.layout, colourway, format })
      return { ...item, post: { ...item.post, family: next.family, layout: next.layout, colourway: next.colourway, content: { ...next.content, ...item.post.content } } }
    }
  }
  return item
}

// For a season's design, the season's next one no other post or Story in
// the campaign is wearing, with the same words
const anotherSeasonal = (item, items, momentId) => {
  const format = item.post.format || 'post'
  const taken = new Set(items.filter(i => i.post && i.id !== item.id && (i.post.format || 'post') === format).map(i => i.post.layout))
  const all = seasonalLayouts(momentId)
  const idx = all.indexOf(item.post.layout)
  const next = all.map((_, k) => all[(idx + 1 + k) % all.length]).find(l => l !== item.post.layout && !taken.has(l) && !seasonalLayout(l).offer)
  if (!next) return item
  return { ...item, post: { ...restyle(item.post, 'layouts', item.post.colourway, next), content: item.post.content } }
}

// A whole campaign in another look: every post's colours change, nothing
// else. Out of the season's look its own designs become plain
// announcements, and come back when it's chosen again.
export const relook = (items, look, { momentId = null, brandColour = null } = {}) => items.map((it) => {
  if (!it.post) return it
  let post = it.post
  if (look !== 'season' && isSeasonal(post.layout)) post = { ...restyle(post, 'editorial'), seasonal: post.layout }
  else if (look === 'season' && post.seasonal && hasSeasonal(momentId)) {
    const { seasonal, ...rest } = post
    post = restyle(rest, 'layouts', rest.colourway, seasonal)
  }
  const colourway = colourwayFor({ look, momentId, brandColour }, post.family, post.type, post.layout)
  const own = makePost(post.type, { trade: post.trade, family: post.family, layout: post.layout, brandColour: look === 'brand' ? brandColour : null }).colourway
  return { ...it, post: { ...post, colourway: colourway || own } }
})
